// Customizable Area Start
import React, { Component, createRef } from "react";
import { Box, IconButton, Typography, styled } from "@mui/material";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import MessageBubbleComponent from "./MessageBubbleComponent.web";

type Props = {
  videoUrl: string;
  time: string;
  date: string;
  senderId: string;
  loggedInUserId: string;
  isSeen: boolean;
};

type State = {
  isPlaying: boolean;
  progress: number;
  duration: number;
  isMuted: boolean;
};

const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
};

const VideoBox = styled(Box)({
  width: "100%",
  position: "relative",
  borderRadius: "8px",
  overflow: "hidden",
  maxWidth: "288px"
});

const Controls = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "5px",
  paddingTop: "8px",
});

const ProgressBar = styled(Box)({
  position: "relative",
  flex: 1,
  height: "6px",
  borderRadius: "4px",
  backgroundColor: "#E2E8F0",
  cursor: "pointer",
  overflow: "hidden",
});

const Progress = styled(Box)({
  position: "absolute",
  height: "100%",
  backgroundColor: "#652784",
  top: 0,
  left: 0,
});

const Duration = styled(Typography)({
  fontWeight: 400,
  fontSize: "12px",
  color: "#652784",
});

class VideoMessage extends Component<Props, State> {
  videoRef = createRef<HTMLVideoElement>();

  state: State = {
    isPlaying: false,
    progress: 0,
    duration: 0,
    isMuted: false,
  };

  componentDidMount() {
    const video = this.videoRef.current;
    if (!video) return;

    video.addEventListener("timeupdate", this.updateProgress);
    video.addEventListener("loadedmetadata", this.updateDuration);
    video.addEventListener("ended", this.handleEnded);
  }

  componentWillUnmount() {
    const video = this.videoRef.current;
    if (!video) return;

    video.removeEventListener("timeupdate", this.updateProgress);
    video.removeEventListener("loadedmetadata", this.updateDuration);
    video.removeEventListener("ended", this.handleEnded);
    document.removeEventListener(
      "fullscreenchange",
      this.handleFullscreenChange
    );
  }

  updateProgress = () => {
    const video = this.videoRef.current;
    if (video) {
      this.setState({ progress: video.currentTime });
    }
  };

  updateDuration = () => {
    const video = this.videoRef.current;
    if (video) {
      this.setState({ duration: video.duration });
    }
  };

  handleEnded = () => {
    this.setState({ isPlaying: false });
  };

  togglePlay = () => {
    const video = this.videoRef.current;
    if (!video) return;

    if (this.state.isPlaying) {
      video.pause();
    } else {
      video.play();
    }

    this.setState((prevState) => ({ isPlaying: !prevState.isPlaying }));
  };

  handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const { duration } = this.state;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * duration;

    if (this.videoRef.current) {
      this.videoRef.current.currentTime = newTime;
    }
  };

  enterFullscreen = () => {
    const video = this.videoRef.current;
    if (!video) return;

    if (video.requestFullscreen) {
      video.requestFullscreen();
    }

    document.addEventListener("fullscreenchange", this.handleFullscreenChange);
  };

  handleFullscreenChange = () => {
    const isFullscreen = !!document.fullscreenElement;
    const video = this.videoRef.current;

    if (!isFullscreen && video) {
      video.pause();
      this.setState({
        isPlaying: false,
        progress: video.currentTime,
        duration: video.duration,
      });

      document.removeEventListener(
        "fullscreenchange",
        this.handleFullscreenChange
      );
    }
  };

  toggleMute = () => {
    const video = this.videoRef.current;
    if (!video) return;

    video.muted = !this.state.isMuted;
    this.setState((prevState) => ({ isMuted: !prevState.isMuted }));
  };

  render() {
    const { videoUrl, time, date, senderId, loggedInUserId, isSeen } =
      this.props;
    const { isPlaying, progress, duration, isMuted } = this.state;

    return (
      <MessageBubbleComponent
        senderId={senderId}
        loggedInUserId={loggedInUserId}
        time={time}
        date={date}
        isSeen={isSeen}
      >
        <VideoBox>
          <video
            ref={this.videoRef}
            src={videoUrl}
            width="100%"
            playsInline
            style={{ borderRadius: 8 }}
          />
        </VideoBox>
        <Controls>
          <IconButton onClick={this.togglePlay} size="small">
            {isPlaying ? (
              <PauseRoundedIcon sx={{ color: "#652784" }} />
            ) : (
              <PlayArrowRoundedIcon sx={{ color: "#652784" }} />
            )}
          </IconButton>

          <IconButton onClick={this.toggleMute} size="small">
            {isMuted ? (
              <VolumeOffIcon fontSize="small" sx={{ color: "#652784" }} />
            ) : (
              <VolumeUpIcon fontSize="small" sx={{ color: "#652784" }} />
            )}
          </IconButton>

          <ProgressBar onClick={this.handleProgressClick}>
            <Progress
              style={{ width: `${(progress / duration) * 100 || 0}%` }}
            />
          </ProgressBar>

          <IconButton onClick={this.enterFullscreen} size="small">
            <FullscreenIcon sx={{ color: "#652784" }} />
          </IconButton>
        </Controls>
        <Duration>{formatTime(progress)} / {formatTime(duration)}</Duration>
      </MessageBubbleComponent>
    );
  }
}

export default VideoMessage;
// Customizable Area End
